import { Badge } from '@crowdfpl/ui-web/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { Check, Trophy } from 'lucide-react';
import {
  formatMatchDate,
  formatMatchDateTime,
  formatMatchDateTimeReversed,
  formatMatchTime,
  getAllOpponents,
  getPlayerImageUrl,
  getTeamShirtUrl,
  isFixtureComplete,
  isFixtureLive,
  type OpponentInfo,
} from '@/helpers/duelCard';
import { isDuelInteractionSuppressed } from '@/helpers/duelInteractionGuard';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import { AvailabilityBadge } from './AvailabilityBadge';
import { PotentialRatingBadges } from './PotentialRatingBadges';
import type { Duel, DuelCardState, DuelPlayer, PotentialRating } from './types';

function isEventFromDialogLayer(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (!el?.closest) return false;
  if (el.closest('[role="dialog"]')) return true;
  if (el.closest('[data-radix-dialog-overlay]')) return true;
  // Fallback for overlay markup when data attribute is missing
  if (el.closest('.fixed.inset-0')) return true;
  return false;
}

function hasAvailabilityFlag(player: DuelPlayer): boolean {
  const pct = player.availability_pct;
  return pct != null && pct !== 100;
}

const SHOW_PLAYER_IMAGE: 'player' | 'shirt' = 'shirt';

interface DuelPlayerBoxProps {
  player: DuelPlayer;
  isPlayer1: boolean;
  duel: Duel;
  totalVotes: number;
  selectedPlayerId: string | null;
  state: DuelCardState;
  disabledSelect: boolean;
  highlighted: boolean;
  playerWon: boolean;
  isDraw: boolean;
  showDebug: boolean;
  solioPredicted?: number;
  onSelect: () => void;
  onHighlightClear: () => void;
}

function MatchInfo({ player, isPlayer1 }: { player: DuelPlayer; isPlayer1: boolean }) {
  const gameweekStats = player.gameweek_stats;
  const fixtures = gameweekStats?.fixtures ?? [];

  if (fixtures.length === 0) return null;

  const minutes = gameweekStats?.minutes ?? 0;
  const points = gameweekStats?.total_points;

  const allNotStarted = fixtures.every((f) => !f.started);
  const anyLive = fixtures.some(isFixtureLive);
  const allFinished = fixtures.every(isFixtureComplete);

  if (allNotStarted && fixtures[0]?.kickoff_time) {
    return (
      <div className={`flex items-center ${isPlayer1 ? '' : 'justify-end'}`}>
        <span className="text-[10px] sm:text-[11px] font-medium text-muted-foreground whitespace-nowrap">
          {isPlayer1 ? formatMatchDateTime(fixtures[0].kickoff_time) : formatMatchDateTimeReversed(fixtures[0].kickoff_time)}
        </span>
      </div>
    );
  }

  if (anyLive && points !== undefined && points !== null) {
    return (
      <div className={`flex items-center gap-1 ${isPlayer1 ? '' : 'flex-row-reverse'}`}>
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
        </span>
        <span className="text-[10px] sm:text-[11px] font-semibold text-foreground whitespace-nowrap animate-pulse">
          {points}pts
        </span>
        <span className="text-[10px] sm:text-[11px] font-medium text-muted-foreground whitespace-nowrap animate-pulse">
          {minutes}min
        </span>
      </div>
    );
  }

  if (allFinished && points !== undefined && points !== null) {
    return (
      <div className={`flex items-center gap-1 ${isPlayer1 ? '' : 'flex-row-reverse'}`}>
        <span className="text-[10px] sm:text-[11px] font-semibold text-foreground whitespace-nowrap">{points}pts</span>
        <span className="text-[10px] sm:text-[11px] font-medium text-muted-foreground whitespace-nowrap">{minutes}min</span>
      </div>
    );
  }

  if (points !== undefined && points !== null && minutes > 0) {
    return (
      <div className={`flex items-center gap-1 ${isPlayer1 ? '' : 'flex-row-reverse'}`}>
        <span className="text-[10px] sm:text-[11px] font-semibold text-foreground whitespace-nowrap">{points}pts</span>
        <span className="text-[10px] sm:text-[11px] font-medium text-muted-foreground whitespace-nowrap">{minutes}min</span>
      </div>
    );
  }

  return null;
}

function PlayerPotentialRating({
  potentialRating,
  votes,
  totalVotes,
  isPlayer1,
  showDebug,
}: {
  potentialRating: PotentialRating;
  votes: { player1_votes: number; player2_votes: number };
  totalVotes: number;
  isPlayer1: boolean;
  showDebug: boolean;
}) {
  const P = totalVotes > 0 ? (isPlayer1 ? votes.player1_votes : votes.player2_votes) / totalVotes : 0;
  const formulaParams = {
    P,
    kBase: potentialRating.kBase,
    kPop: potentialRating.kPop,
    kEarly: potentialRating.kEarly,
    ratingMultiplier: potentialRating.rating_multiplier ?? 1,
  };
  const [win, lose, tie] = isPlayer1
    ? [potentialRating.if_player_a_wins, potentialRating.if_player_a_loses, potentialRating.if_player_a_tie]
    : [potentialRating.if_player_b_wins, potentialRating.if_player_b_loses, potentialRating.if_player_b_tie];

  return (
    <PotentialRatingBadges
      win={win}
      lose={lose}
      tie={tie}
      isPlayer1={isPlayer1}
      formulaParams={formulaParams}
      totalVotes={totalVotes}
      showDebug={showDebug}
    />
  );
}

export function DuelPlayerBox({
  player,
  isPlayer1,
  duel,
  totalVotes,
  selectedPlayerId,
  state,
  disabledSelect,
  highlighted,
  playerWon,
  isDraw,
  showDebug,
  solioPredicted,
  onSelect,
  onHighlightClear,
}: DuelPlayerBoxProps) {
  const shirtUrl = getTeamShirtUrl(player.team);
  const playerImg = getPlayerImageUrl(player.photo);
  const showPlayer = SHOW_PLAYER_IMAGE === 'player';
  const showShirt = SHOW_PLAYER_IMAGE === 'shirt';
  const opponents: OpponentInfo[] = getAllOpponents(player);
  const side = isPlayer1 ? 'player1' : 'player2';
  const isSelected = selectedPlayerId === side;
  const isMobile = useIsMobile();
  const hasSolioPrediction = solioPredicted !== undefined && solioPredicted !== null;
  /** On mobile only: show availability icon in the Vote slot (next to Vote/Voted). On desktop we use absolute position over jersey. */
  const showAvailabilityInSlot = isMobile && hasAvailabilityFlag(player);

  const boxClassName = `player-box relative overflow-hidden p-1.5 rounded-lg border transition-all duration-300 min-h-[120px] flex flex-col ${disabledSelect ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
    } ${isSelected
      ? 'border-primary bg-primary/5 shadow-sm'
      : selectedPlayerId && !isSelected
        ? 'border-border opacity-60'
        : state === 'active'
          ? 'border-border md:hover:border-primary/50 md:hover:bg-primary/5'
          : 'border-border'
    } ${highlighted && state === 'active' ? 'player-box--highlight' : ''}`;

  // Absolute shirt: slight push + text gutter so sleeve clears copy without a huge gap.
  const imgPosition = isPlayer1
    ? { left: 0, transform: 'translateX(-50%)' }
    : { right: 0, transform: 'translateX(50%)' };
  const imgClass = isPlayer1 ? 'absolute left-0 object-cover opacity-80' : 'absolute right-0 object-cover opacity-80';

  return (
    <div
      className={boxClassName}
      role="button"
      tabIndex={0}
      aria-label={`Vote for ${player.name} (${player.team.short_name})`}
      onClick={(e) => {
        if (isEventFromDialogLayer(e.target)) return;
        if (isDuelInteractionSuppressed()) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onHighlightClear();
        onSelect();
      }}
      onMouseDown={(e) => {
        if (isEventFromDialogLayer(e.target)) return;
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        if (isEventFromDialogLayer(e.target)) return;
        e.stopPropagation();
      }}
    >
      {/* Badge slot top - Vote/Voted. On mobile only: availability icon next to the button (items-center for badge alignment). */}
      <div className={`relative z-10 h-6 flex items-center gap-1 mb-1 ${isPlayer1 ? 'justify-end pr-1' : 'justify-start pl-1'}`}>
        {isPlayer1 && showAvailabilityInSlot && (
          <AvailabilityBadge player={player} position="left" inline />
        )}
        {isSelected ? (
          <span className="inline-flex items-center rounded-full px-1 py-0.5 text-[10px] font-medium border bg-badge-voted text-badge-voted-foreground border-badge-voted-border">
            Voted
          </span>
        ) : (
          state === 'active' &&
          !selectedPlayerId && (
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border border-primary/30"
              style={{ backgroundColor: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' }}
            >
              Vote
            </span>
          )
        )}
        {!isPlayer1 && showAvailabilityInSlot && (
          <AvailabilityBadge player={player} position="right" inline />
        )}
      </div>

      {/* Image: player or shirt */}
      {showPlayer && playerImg ? (
        <img
          src={playerImg}
          alt={player.name}
          className={imgClass}
          style={{ height: '100%', top: '10%', ...imgPosition, objectPosition: 'center', overflow: 'visible' }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (shirtUrl) target.src = shirtUrl;
            else target.style.display = 'none';
          }}
        />
      ) : (showShirt || showPlayer) && shirtUrl ? (
        <img
          src={shirtUrl}
          alt={player.team.short_name}
          className={imgClass}
          style={{ height: '100%', top: '10%', ...imgPosition, objectPosition: 'center', overflow: 'visible' }}
        />
      ) : null}

      {/* Winner/Draw badge */}
      <div className={`absolute top-2 z-30 flex items-center gap-1 ${isPlayer1 ? 'left-2' : 'right-2'}`}>
        {state === 'resolved' && isDraw && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-badge-draw text-badge-draw-foreground">
            Draw
          </span>
        )}
        {state === 'resolved' && playerWon && !isDraw && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-badge-win text-badge-win-foreground">
            <Trophy className="h-3 w-3" />
            Win
          </span>
        )}
      </div>

      {/* Desktop: availability flag over jersey (tooltip on hover). Mobile: icon is in the Vote slot above. */}
      {!isMobile && hasAvailabilityFlag(player) && (
        <AvailabilityBadge player={player} position={isPlayer1 ? 'left' : 'right'} />
      )}
      {opponents.length > 0 && (
        <div className={`absolute bottom-1.5 z-20 flex flex-col gap-0.5 ${isPlayer1 ? 'left-1' : 'right-1 items-end'}`}>
          {opponents.map((opp, idx) => {
            const tooltipContent = (
              <div className="text-xs">
                {opp.fixture.team_h?.short_name} vs {opp.fixture.team_a?.short_name}
                {opp.fixture.team_h_score != null && opp.fixture.team_a_score != null && (
                  <span className="ml-1 font-semibold">
                    {opp.fixture.team_h_score} - {opp.fixture.team_a_score}
                  </span>
                )}
                {opp.fixture.kickoff_time && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {formatMatchDate(opp.fixture.kickoff_time)} {formatMatchTime(opp.fixture.kickoff_time)}
                  </div>
                )}
              </div>
            );
            return (
              <TooltipProvider key={idx}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Badge
                        variant="outline"
                        className="text-[8px] px-1.5 py-0 h-4 bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800 cursor-help"
                      >
                        {opp.opponentShortName}({opp.homeAway})
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs z-[100]">
                    {tooltipContent}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      )}

      {/* Content (name, position, match info, potential rating) */}
      <div className={`relative z-10 flex min-w-0 flex-1 flex-col ${isPlayer1 ? 'ml-14' : 'mr-14 text-right'}`}>
        <div className={`mb-1.5 flex min-w-0 items-center gap-1.5 ${isPlayer1 ? '' : 'justify-end'}`}>
          <h3 className="player-name min-w-0 flex-1 truncate font-semibold" title={player.name}>
            {player.name}
          </h3>
          {isSelected && (SHOW_PLAYER_IMAGE === 'player' ? !playerImg : !shirtUrl) && (
            <Check className="h-3 w-3 text-primary flex-shrink-0" />
          )}
        </div>
        <div className={`flex items-center gap-1.5 flex-wrap mb-0.5 ${isPlayer1 ? '' : 'justify-end'}`}>
          {isPlayer1 && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-primary">
              {player.position}
            </Badge>
          )}
          {player.nowCost !== undefined && (
            <span className="text-[11px] font-medium text-foreground">£{(player.nowCost / 10).toFixed(1)}m</span>
          )}
          {!isPlayer1 && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-primary">
              {player.position}
            </Badge>
          )}
        </div>
        <div className={`mt-auto flex flex-col gap-0.5 ${isPlayer1 ? '' : 'items-end'}`}>
          <MatchInfo player={player} isPlayer1={isPlayer1} />
          <div className="min-h-[20px]">
            {hasSolioPrediction ? (
              <a
                href="https://fpl.solioanalytics.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1 mt-0 px-1.5 rounded-md border border-border bg-muted/70 w-fit hover:bg-muted transition-colors ${isPlayer1 ? '' : 'flex-row-reverse'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <img src="/icons/solio-logo.svg" alt="Solio" className="h-2.5 w-auto opacity-80 shrink-0" />
                <span className="text-[10px] font-semibold text-foreground/80 tabular-nums">{solioPredicted.toFixed(1)}</span>
                <span className="text-[8px] text-muted-foreground">proj.</span>
              </a>
            ) : (
              <div className="h-[20px]" aria-hidden="true" />
            )}
          </div>
          {duel.potential_rating && (
            <div className={isPlayer1 ? 'flex items-center gap-1.5 flex-wrap' : 'flex items-center gap-1.5 flex-wrap flex-row-reverse'}>
              <PlayerPotentialRating
                potentialRating={duel.potential_rating}
                votes={duel.votes}
                totalVotes={totalVotes}
                isPlayer1={isPlayer1}
                showDebug={showDebug}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
